import { useMemo, useState } from 'react';

// ─────────────────────────────────────────────────────────────
// Affichage riche des réponses IA.
// Les modèles renvoient souvent des tableaux markdown « bruts » (très
// archaïques à l'écran). Ici, on les transforme en cartes de données :
//   • graphique en barres groupées (comparaison visuelle immédiate),
//   • tableau interactif (recherche + tri par colonne),
//   • bascule Graphique / Tableau.
// Aucun rendu externe n'est nécessaire : tout est calculé localement.
// ─────────────────────────────────────────────────────────────

interface TableBlock {
  type: 'table';
  headers: string[];
  rows: string[][];
}
interface TextBlock { type: 'text'; text: string }
interface HeadingBlock { type: 'heading'; text: string }
interface ListBlock { type: 'list'; items: string[]; ordered: boolean }

type Block = TableBlock | TextBlock | HeadingBlock | ListBlock;

const SEPARATOR_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/;

/** Nettoie une cellule markdown (gras, code, italique). */
function cleanCell(value: string): string {
  return value
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/^\s*|\s*$/g, '');
}

/** Sépare une ligne de tableau markdown en cellules. */
function splitRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((c) => cleanCell(c));
}

/** Découpe un texte IA en blocs affichables. */
function parseBlocks(content: string): Block[] {
  const lines = (content || '').replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { items: string[]; ordered: boolean } | null = null;

  const flushParagraph = () => {
    const text = paragraph.join('\n').trim();
    if (text) blocks.push({ type: 'text', text });
    paragraph = [];
  };
  const flushList = () => {
    if (list && list.items.length > 0) blocks.push({ type: 'list', ...list });
    list = null;
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    // Tableau : ligne d'en-tête suivie d'une ligne de séparation.
    if (line.includes('|') && i + 1 < lines.length && SEPARATOR_RE.test(lines[i + 1])) {
      flushParagraph();
      flushList();
      const headers = splitRow(line);
      const rows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && lines[j].includes('|') && lines[j].trim() !== '') {
        const cells = splitRow(lines[j]);
        if (cells.some((c) => c.length > 0)) rows.push(cells);
        j += 1;
      }
      if (headers.length > 0) blocks.push({ type: 'table', headers, rows });
      i = j - 1;
      continue;
    }

    // Titres markdown.
    if (/^#{1,6}\s+/.test(line)) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', text: cleanCell(line.replace(/^#{1,6}\s+/, '')) });
      continue;
    }

    // Listes à puces / numérotées.
    const bullet = line.match(/^\s*[-•*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      flushParagraph();
      const ordered = Boolean(numbered);
      if (!list) list = { items: [], ordered };
      list.items.push((bullet ? bullet[1] : numbered?.[1]) || '');
      continue;
    }

    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }

    flushList();
    paragraph.push(line);
  }
  flushParagraph();
  flushList();
  return blocks;
}

/** Convertit une cellule en nombre, ou null si ce n'en est pas un. */
function toNumber(raw: string): number | null {
  const text = raw.replace(/\u00a0/g, ' ');
  if (!/\d/.test(text)) return null;
  // On garde les chiffres, le signe, la virgule et le point décimaux.
  const normalized = text
    .replace(/[^\d,.\-]/g, '')
    .replace(/,(?=\d{3}\b)/g, '')
    .replace(',', '.');
  if (!normalized || normalized === '-' || normalized === '.' || normalized === ',') return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

function formatNumber(value: number): string {
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
}

const METRIC_STYLES = [
  'bg-primary-500',
  'bg-accent-500',
  'bg-secondary-500',
];

interface AiDataTableProps {
  headers: string[];
  rows: string[][];
}

/** Tableau de données IA : graphique + tri + recherche. */
function AiDataTable({ headers, rows }: AiDataTableProps) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ col: number; dir: 1 | -1 } | null>(null);

  // Colonnes numériques (hors première colonne de libellé).
  const numericCols = useMemo(() => {
    const out: number[] = [];
    for (let c = 1; c < headers.length; c += 1) {
      const values = rows.map((r) => toNumber(r[c] ?? ''));
      const filled = values.filter((v): v is number => v !== null);
      if (filled.length >= Math.max(1, Math.ceil(rows.length * 0.5))) out.push(c);
    }
    return out.slice(0, 3);
  }, [headers, rows]);

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = rows.map((cells, index) => ({ cells, index }));
    if (q) {
      list = list.filter(({ cells }) => cells.some((c) => c.toLowerCase().includes(q)));
    }
    if (sort) {
      list = [...list].sort((a, b) => {
        const av = toNumber(a.cells[sort.col] ?? '');
        const bv = toNumber(b.cells[sort.col] ?? '');
        if (av !== null && bv !== null) return (av - bv) * sort.dir;
        return String(a.cells[sort.col] ?? '').localeCompare(String(b.cells[sort.col] ?? ''), 'fr') * sort.dir;
      });
    }
    return list;
  }, [rows, query, sort]);

  const maxByCol = useMemo(() => {
    const map: Record<number, number> = {};
    numericCols.forEach((c) => {
      map[c] = rows.reduce((max, r) => Math.max(max, Math.abs(toNumber(r[c] ?? '') ?? 0)), 0) || 1;
    });
    return map;
  }, [numericCols, rows]);

  const toggleSort = (col: number) => {
    setSort((prev) => (prev && prev.col === col ? { col, dir: prev.dir === 1 ? -1 : 1 } : { col, dir: -1 }));
  };

  if (headers.length === 0) return null;

  return (
    <div className="my-2 rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-background-200/60 bg-background-100/60">
        <div className="relative flex-1 min-w-[140px]">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filtrer les lignes…"
            className="w-full pl-7 pr-2 py-1.5 rounded-md border border-background-200/70 bg-background-50 text-[11px] text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div className="flex items-center gap-0.5 bg-background-200/60 rounded-full p-0.5">
          <button
            type="button"
            onClick={() => setView('chart')}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${view === 'chart' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            <i className="ri-bar-chart-grouped-line text-xs"></i>Graphique
          </button>
          <button
            type="button"
            onClick={() => setView('table')}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${view === 'table' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            <i className="ri-table-line text-xs"></i>Tableau
          </button>
        </div>
        <span className="text-[10px] text-foreground-400 whitespace-nowrap">{visibleRows.length}/{rows.length}</span>
      </div>

      {numericCols.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 border-b border-background-200/50">
          {numericCols.map((c, idx) => (
            <span key={c} className="inline-flex items-center gap-1 text-[10px] text-foreground-600 whitespace-nowrap">
              <span className={`w-2 h-2 rounded-sm ${METRIC_STYLES[idx % METRIC_STYLES.length]}`}></span>
              {headers[c]}
            </span>
          ))}
        </div>
      ) : null}

      {view === 'chart' && numericCols.length > 0 ? (
        <div className="p-3 space-y-2.5 max-h-80 overflow-y-auto">
          {visibleRows.length === 0 ? (
            <p className="text-[11px] text-foreground-400 text-center py-4">Aucune ligne ne correspond au filtre.</p>
          ) : (
            visibleRows.map(({ cells, index }) => (
              <div key={index}>
                <p className="text-[11px] font-medium text-foreground-700 truncate mb-1">{cells[0] || `Ligne ${index + 1}`}</p>
                <div className="space-y-1">
                  {numericCols.map((c, idx) => {
                    const value = toNumber(cells[c] ?? '');
                    if (value === null) return null;
                    const width = Math.max(3, Math.round((Math.abs(value) / maxByCol[c]) * 100));
                    return (
                      <div key={c} className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-background-200/60 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${METRIC_STYLES[idx % METRIC_STYLES.length]} transition-all`}
                            style={{ width: `${width}%` }}
                          ></div>
                        </div>
                        <span className="text-[10px] font-semibold text-foreground-700 w-20 text-right tabular-nums">{cells[c]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="overflow-x-auto max-h-80">
          <table className="w-full text-[11px] border-collapse">
            <thead className="sticky top-0 bg-background-100/95 backdrop-blur">
              <tr>
                {headers.map((h, c) => (
                  <th
                    key={`${h}-${c}`}
                    onClick={() => toggleSort(c)}
                    className="px-2.5 py-2 text-left font-semibold text-foreground-600 whitespace-nowrap cursor-pointer hover:text-foreground-900 transition-colors select-none"
                    title="Trier"
                  >
                    <span className="inline-flex items-center gap-1">
                      {h}
                      {sort?.col === c && (
                        sort.dir === 1
                          ? <i className="ri-arrow-up-line text-[10px] text-primary-600"></i>
                          : <i className="ri-arrow-down-line text-[10px] text-primary-600"></i>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={headers.length} className="px-2.5 py-4 text-center text-foreground-400">Aucune ligne.</td>
                </tr>
              ) : (
                visibleRows.map(({ cells, index }) => (
                  <tr key={index} className="border-t border-background-200/50 odd:bg-background-100/30">
                    {headers.map((_, c) => (
                      <td
                        key={c}
                        className={`px-2.5 py-1.5 text-foreground-800 ${c === 0 ? 'font-medium' : 'tabular-nums'} whitespace-nowrap`}
                      >
                        {cells[c] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Texte enrichi : gras **…** et sauts de ligne conservés. */
function RichText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p className="text-sm leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">
      {parts.map((part, i) => (
        part.startsWith('**') && part.endsWith('**') && part.length > 4
          ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
          : <span key={i}>{part}</span>
      ))}
    </p>
  );
}

interface AiRichContentProps {
  content: string;
  className?: string;
}

/**
 * Rendu des réponses IA : tableaux transformés en cartes interactives,
 * titres, listes et paragraphes lisibles.
 */
export default function AiRichContent({ content, className = '' }: AiRichContentProps) {
  const blocks = useMemo(() => parseBlocks(content), [content]);

  return (
    <div className={`space-y-1.5 ${className}`}>
      {blocks.map((block, i) => {
        if (block.type === 'table') {
          return <AiDataTable key={i} headers={block.headers} rows={block.rows} />;
        }
        if (block.type === 'heading') {
          return (
            <p key={i} className="text-[13px] font-bold text-foreground-900 mt-2 first:mt-0 flex items-center gap-1.5">
              <span className="w-1 h-3.5 rounded-full bg-accent-500"></span>
              {block.text}
            </p>
          );
        }
        if (block.type === 'list') {
          return (
            <ul key={i} className="space-y-1 pl-0.5">
              {block.items.map((item, j) => (
                <li key={j} className="flex items-start gap-1.5 text-sm leading-relaxed">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                  <span className="min-w-0 flex-1">
                    <RichTextInline text={item} />
                  </span>
                </li>
              ))}
            </ul>
          );
        }
        return <RichText key={i} text={block.text} />;
      })}
    </div>
  );
}

function RichTextInline({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => (
        part.startsWith('**') && part.endsWith('**') && part.length > 4
          ? <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>
          : <span key={i}>{part}</span>
      ))}
    </>
  );
}